import type { UseQueryResult } from '@tanstack/react-query';
import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { PARTNER_COMPANIES_ALL_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CompanyItem {
  companyId: string;
  companyName: string;
  organizationId: string;
}

export interface UsePartnerCompaniesAllParams {
  /** Maximum number of companies to return. No limit applied when omitted. */
  limit?: number;
  /**
   * AceUpAdmin/AceUpViewer only — scopes the request to this organization.
   * The backend returns an unscoped, cross-tenant list when omitted (it does
   * *not* fall back to the caller's own org_id from the JWT for this
   * endpoint) — so without this param the company filter dropdown would show
   * every organization's companies at once instead of just the selected
   * org's. Ignored (and unnecessary) for PartnerAdmin, whose org_id always
   * comes from the JWT.
   */
  organizationId?: string | null;
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches all companies visible to the caller — lightweight (names only, no
 * stats). Suitable for populating filter dropdowns without date-range overhead.
 *
 * Backed by GET /api/v1/partner/companies/all.
 */
export const usePartnerCompaniesAllQuery = ({
  limit,
  organizationId,
  enabled = true,
}: UsePartnerCompaniesAllParams = {}): UseQueryResult<CompanyItem[], Error> =>
  useBasicQuery<CompanyItem[]>(
    PARTNER_COMPANIES_ALL_PATH,
    {
      limit: limit ?? undefined,
      organization_id: organizationId || undefined,
    },
    { enabled }
  );
